// src/pages/finance/MassPaymentForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { es } from 'date-fns/locale';

const MassPaymentForm = ({ open, onClose, onSubmit, invoices = [], paymentMethods = [], currencySymbol = '$' }) => {
  const [formData, setFormData] = useState({
    payment_date: new Date(),
    payment_method: '',
    payment_reference: '',
    proof_image_url: '',
    activate_service: true
  });

  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [ocrAnalyzing, setOcrAnalyzing] = useState(false);
  const [ocrStatus, setOcrStatus] = useState(null);
  const [ocrMessage, setOcrMessage] = useState('');

  // Calcular totales
  const totalPending = invoices.reduce((sum, inv) => {
    const total = parseFloat(inv.amount || 0);
    const paid = parseFloat(inv.paid_amount || 0);
    return sum + Math.max(0, total - paid);
  }, 0);

  // Obtener el nombre del cliente único o una lista si son varios
  const uniqueClients = [...new Set(invoices.map(inv => inv.client_name))];
  const clientText = uniqueClients.join(', ');

  // Utilidad de compresión de imágenes
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const MAX_WIDTH = 1000;
          const MAX_HEIGHT = 1000;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Error al comprimir la imagen'));
            }
          }, 'image/jpeg', 0.75);
        };
        img.onerror = (err) => reject(new Error('Error al cargar la imagen en memoria'));
      };
      reader.onerror = (err) => reject(new Error('Error al leer el archivo'));
    });
  };

  const processOcrAndUpload = async (file) => {
    if (!file) return;

    setOcrAnalyzing(true);
    setOcrStatus(null);
    setOcrMessage('');

    try {
      const token = localStorage.getItem('token');
      
      // Comprimir la imagen antes de subirla
      let fileToUpload = file;
      try {
        fileToUpload = await compressImage(file);
        console.log('✅ Imagen comprimida para OCR masivo. Tamaño original:', file.size, 'Comprimido:', fileToUpload.size);
      } catch (compressError) {
        console.error('Error al comprimir imagen, subiendo original:', compressError);
      }

      // Determinar si el método seleccionado actualmente es efectivo
      const selectedMethodObj = paymentMethods.find(m => m.id === formData.payment_method);
      const isCash = selectedMethodObj ? selectedMethodObj.name.toLowerCase().includes('efectivo') : false;

      const uploadFormData = new FormData();
      uploadFormData.append('proof', fileToUpload);
      
      // Pasar el ID del primer recibo de la lista como referencia para el cliente
      const clientId = invoices.length > 0 ? invoices[0].client_id : '';
      const queryParams = new URLSearchParams();
      if (isCash) queryParams.append('isCash', 'true');
      if (clientId) queryParams.append('clientId', clientId);

      const response = await fetch(`/api/payments/upload?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al subir el comprobante.');
      }

      const data = await response.json();
      console.log('[OCR Mass Result]', data);

      setFormData(prev => ({
        ...prev,
        proof_image_url: data.proof_image_url
      }));

      const ocr = data.ocr || {};
      if (ocr.success && !isCash) {
        setOcrStatus('success');
        setOcrMessage('¡Comprobante escaneado con éxito! Se han autocompletado los campos.');
        
        setFormData(prev => {
          const updated = { ...prev };
          if (ocr.reference) updated.payment_reference = ocr.reference;
          
          if (ocr.method && ocr.method !== 'unknown') {
            const cleanOcrMethod = ocr.method.toLowerCase();
            const foundMethod = paymentMethods.find(m => {
              const cleanName = m.name.toLowerCase();
              return cleanName.includes(cleanOcrMethod) || 
                     (cleanOcrMethod === 'yape' && cleanName.includes('yape')) ||
                     (cleanOcrMethod === 'plin' && cleanName.includes('plin')) ||
                     (cleanOcrMethod === 'bcp' && (cleanName.includes('bcp') || cleanName.includes('crédito'))) ||
                     (cleanOcrMethod === 'bbva' && (cleanName.includes('bbva') || cleanName.includes('continental'))) ||
                     (cleanOcrMethod === 'interbank' && cleanName.includes('interbank'));
            });
            if (foundMethod) {
              updated.payment_method = foundMethod.id;
            }
          }
          return updated;
        });
      } else {
        setOcrStatus(isCash ? null : 'failed');
        if (!isCash) {
          setOcrMessage('No pudimos leer los datos automáticamente. Por favor completa los campos manualmente.');
        }
      }
    } catch (err) {
      console.error('[OCR Mass Error]', err);
      setOcrStatus('failed');
      setOcrMessage(err.message || 'Error al procesar el comprobante.');
    } finally {
      setOcrAnalyzing(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      processOcrAndUpload(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.payment_method) {
      setError('Debe seleccionar un método de pago');
      return;
    }

    onSubmit({
      paymentMethod: formData.payment_method,
      paymentReference: formData.payment_reference,
      proofImageUrl: formData.proof_image_url,
      activateService: formData.activate_service
    });
  };

  if (!open || invoices.length === 0) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>
          Registrar Pago Masivo en Lote
        </DialogTitle>
        
        <DialogContent sx={{ mt: 1 }}>
          <Box className="space-y-4 py-2">
            {error && (
              <Alert severity="error" sx={{ borderRadius: '14px', mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* INFORMACIÓN DEL CLIENTE Y MONTO TOTAL */}
            <Box 
              sx={{ 
                p: 3, 
                borderRadius: '16px', 
                backgroundColor: 'rgba(99, 102, 241, 0.05)', 
                border: '1px solid rgba(99, 102, 241, 0.1)',
                mb: 3
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
                    Cliente(s) Seleccionado(s)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {clientText}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
                    Monto Total a Cobrar (Suma de Saldos)
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#10b981' }}>
                    {currencySymbol} {totalPending.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* LISTA DE RECIBOS SELECCIONADOS */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
              Recibos Incluidos en el Pago ({invoices.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden', mb: 3, maxHeight: '200px' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>N° Recibo</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>Monto Total</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>Saldo Pendiente</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => {
                    const total = parseFloat(inv.amount || 0);
                    const paid = parseFloat(inv.paid_amount || 0);
                    const pending = Math.max(0, total - paid);
                    return (
                      <TableRow key={inv.id}>
                        <TableCell sx={{ fontWeight: 600 }}>{inv.invoice_number}</TableCell>
                        <TableCell>{inv.client_name}</TableCell>
                        <TableCell>{currencySymbol} {total.toFixed(2)}</TableCell>
                        <TableCell sx={{ color: '#ef4444', fontWeight: 600 }}>{currencySymbol} {pending.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 3 }} />

            {/* FORMULARIO DE COBRO */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Método de Pago"
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                >
                  {paymentMethods.map(method => (
                    <MenuItem key={method.id} value={method.id}>
                      {method.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Referencia / Operación Única"
                  name="payment_reference"
                  value={formData.payment_reference}
                  onChange={handleChange}
                  placeholder="Nro. de Operación, Transferencia, etc."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <DateTimePicker
                    label="Fecha y Hora del Pago"
                    value={formData.payment_date}
                    onChange={(date) => handleChange({
                      target: { name: 'payment_date', value: date }
                    })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* UPLOAD Y COMPRESIÓN DE VOUCHER */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  disabled={ocrAnalyzing}
                  sx={{ borderRadius: '12px', borderStyle: 'dashed', py: 1.5, textTransform: 'none' }}
                >
                  {ocrAnalyzing ? 'Subiendo y analizando comprobante...' : 'Subir Foto del Comprobante Único'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    disabled={ocrAnalyzing}
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFile && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 600, color: '#6366f1' }}>
                    Archivo cargado: {selectedFile.name}
                  </Typography>
                )}
              </Grid>

              {ocrAnalyzing && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'rgba(99, 102, 241, 0.05)', borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                    <CircularProgress size={20} color="primary" />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#4f46e5' }}>
                      Analizando comprobante con lector inteligente OCR...
                    </Typography>
                  </Box>
                </Grid>
              )}

              {ocrStatus === 'success' && ocrMessage && (
                <Grid item xs={12}>
                  <Alert severity="success" sx={{ borderRadius: '12px' }}>
                    {ocrMessage}
                  </Alert>
                </Grid>
              )}

              {ocrStatus === 'failed' && ocrMessage && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                    {ocrMessage}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activate_service}
                      onChange={handleChange}
                      name="activate_service"
                      color="primary"
                    />
                  }
                  label="Re-activar automáticamente el servicio WISP al procesar cobros"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={ocrAnalyzing}
            sx={{ 
              borderRadius: '12px', 
              textTransform: 'none', 
              px: 3, 
              fontWeight: 700, 
              backgroundColor: '#6366f1', 
              '&:hover': { backgroundColor: '#4f46e5' } 
            }}
          >
            {ocrAnalyzing ? 'Procesando OCR...' : 'Registrar Pago Masivo'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MassPaymentForm;
